import { httpClient } from '@/services/http/httpClient'
import { getTenantByCookies } from '@/utils/getTenantByCookies'

type TopTenUserHttpResponse = {
  id: string
  name: string
  image: string | null
  views: number
  comments: number
  ideaLikes: number
  movies: number
  ideasDraft: number
  total: number
}

type TopTenUser = {
  id: string
  sequence: string
  name: string
  avatarUrl?: string
  views: number
  comments: number
  ideaLikes: number
  movies: number
  ideasDraft: number
  total: number
}

type Params = {
  startDate?: Date
  endDate?: Date
  challenges?: string[]
}

type Response = {
  topTenUsers: TopTenUser[]
}

async function getDashboardTopTenUsers({
  startDate,
  endDate,
  challenges,
}: Params): Promise<Response> {
  const tenant = await getTenantByCookies()

  if (tenant?.slug === 'trial') {
    await new Promise((resolve) => setTimeout(resolve, 1000))

    return {
      topTenUsers: [
        {
          id: '1c0b9881-064f-4a5e-97c3-ac6ff1af73b5',
          sequence: '1',
          name: 'Pedro Lucas',
          views: 45,
          comments: 12,
          ideaLikes: 28,
          movies: 18,
          ideasDraft: 3,
          total: 106,
        },
        {
          id: '26b9a677-8083-4364-8d39-340132a27c9c',
          sequence: '2',
          name: 'Evelyn Monteiro',
          views: 38,
          comments: 8,
          ideaLikes: 22,
          movies: 15,
          ideasDraft: 2,
          total: 85,
        },
        {
          id: 'a72aeda0-bccc-4129-8353-2e59c44fcc3c',
          sequence: '3',
          name: 'Patrick Louback',
          views: 32,
          comments: 6,
          ideaLikes: 18,
          movies: 12,
          ideasDraft: 1,
          total: 69,
        },
        {
          id: '1884242a-8d54-480c-9fb7-3f7964f0fb68',
          sequence: '4',
          name: 'Jessica Hakime',
          views: 28,
          comments: 5,
          ideaLikes: 15,
          movies: 10,
          ideasDraft: 1,
          total: 59,
        },
        {
          id: '9f3bd8c9-4a8a-49f0-9ff8-ac0cc927b473',
          sequence: '5',
          name: 'Vinicius Arantes',
          views: 24,
          comments: 4,
          ideaLikes: 12,
          movies: 8,
          ideasDraft: 1,
          total: 49,
        },
        {
          id: '7b242092-69dd-4b11-a390-24ad40c586c2',
          sequence: '6',
          name: 'Henrique Moreira',
          views: 18,
          comments: 3,
          ideaLikes: 9,
          movies: 6,
          ideasDraft: 0,
          total: 36,
        },
        {
          id: '79c3ccdf-f2ec-48f1-bef4-dc174ebfaf54',
          sequence: '7',
          name: 'Bruno Moreira',
          avatarUrl:
            'https://media.licdn.com/dms/image/v2/D4D03AQHt-wlrsWSlnA/profile-displayphoto-crop_800_800/B4DZhOB4L5GQAI-/0/1753655772446?e=1761782400&v=beta&t=i6N47wOZ1NP_Cuz7EOyqx71zlYK1mGeH3biRAhkt5Aw',
          views: 15,
          comments: 2,
          ideaLikes: 7,
          movies: 4,
          ideasDraft: 0,
          total: 28,
        },
        {
          id: '4a0d8f39-a993-4b18-bdba-00e8964335b2',
          sequence: '8',
          name: 'Vinícius Scarpa',
          avatarUrl:
            'https://media.licdn.com/dms/image/v2/C4D03AQG2vGiaakWBZA/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1572343143609?e=1761782400&v=beta&t=ysUX5zKALGQVMuxEE-uX0v7mg6ysxj40ZL7LIKDEpyQ',
          views: 12,
          comments: 1,
          ideaLikes: 5,
          movies: 3,
          ideasDraft: 0,
          total: 21,
        },
        {
          id: '0d4c20fa-2e2e-4c23-bc1a-7faec8afe4b6',
          sequence: '9',
          name: 'Denise Barbetti',
          avatarUrl:
            'https://media.licdn.com/dms/image/v2/D4D03AQHhROewbW_rNQ/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1713479131792?e=1761782400&v=beta&t=w1HMxYtIiyd3aZJ62f23MaQqZJwLg4TqB0NXxND80Gg',
          views: 8,
          comments: 1,
          ideaLikes: 3,
          movies: 2,
          ideasDraft: 0,
          total: 14,
        },
        {
          id: 'ca0fd53e-4550-41f4-aefd-48d82ea585bb',
          sequence: '10',
          name: 'Vinicius Sousa',
          avatarUrl:
            'https://media.licdn.com/dms/image/v2/D4D03AQEHRSVLCcjjzA/profile-displayphoto-shrink_800_800/B4DZZEr0jJHIAg-/0/1744909060752?e=1761782400&v=beta&t=dLozACBexeXUzl8SEeqjrdaY_bVNogTAPwxrpNKEVjc',
          views: 5,
          comments: 0,
          ideaLikes: 2,
          movies: 1,
          ideasDraft: 0,
          total: 8,
        },
      ],
    }
  }

  const queryParams = new URLSearchParams({
    ...(startDate && { startDate: startDate.toISOString() }),
    ...(endDate && { endDate: endDate.toISOString() }),
    ...(challenges && { campaignIds: JSON.stringify(challenges) }),
  })

  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''

  const { data = [] } = await httpClient.get<TopTenUserHttpResponse[]>(
    `/users/dashboard/detailed-ranking${query}`,
  )

  return {
    topTenUsers: data.map((user, index) => ({
      id: user.id,
      sequence: `${index + 1}`,
      name: user.name,
      avatarUrl: user?.image ?? undefined,
      views: user.views,
      comments: user.comments,
      ideaLikes: user.ideaLikes,
      movies: user.movies,
      ideasDraft: user.ideasDraft,
      total: user.total,
    })),
  }
}

export { getDashboardTopTenUsers, type TopTenUser }
